import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
const input =
  "mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white px-4";
const empty = {
  date: "",
  expenseLedgerId: "",
  taxTreatment: "",
  reason: "",
  taxReviewNote: "",
  evidenceReference: "",
};
export default function ReturnLosses({ company, returnId, onPosted }) {
  const { accessToken, user } = useAuth(),
    actor = user?.id || user?.sub,
    canRequest = hasPermission(user, "accounting.vouchers_post"),
    canApprove = hasPermission(user, "accounting.return_loss_approve");
  const [data, setData] = useState(null),
    [form, setForm] = useState(empty),
    [preview, setPreview] = useState(null),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [selected, setSelected] = useState(""),
    [approvalNote, setApprovalNote] = useState(""),
    [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const generation = useRef(0),
    requestKey = useRef(null),
    base = `/accounting/${company}/supplier-returns/${returnId}`;
  useEffect(() => {
    const requests = generation;
    requests.current++;
    setData(null);
    setForm(empty);
    setPreview(null);
    setConfirmed(false);
    setBusy(false);
    setError("");
    setNotice("");
    setSelected("");
    setApprovalNote("");
    setApprovalConfirmed(false);
    requestKey.current = null;
    return () => {
      requests.current++;
    };
  }, [company, returnId, accessToken]);
  async function load() {
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    try {
      const r = await apiRequest(`${base}/losses`, { accessToken });
      if (seq === generation.current) {
        setData(r);
        setPreview(null);
        setConfirmed(false);
        requestKey.current = null;
      }
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  function change(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    setPreview(null);
    setConfirmed(false);
    requestKey.current = null;
    setError("");
    setNotice("");
  }
  async function review(e) {
    e.preventDefault();
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    setPreview(null);
    setConfirmed(false);
    try {
      const r = await apiRequest(`${base}/loss-preview`, {
        method: "POST",
        accessToken,
        body: form,
      });
      if (seq === generation.current) setPreview(r);
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  async function request() {
    if (busy || !canRequest || !preview || !confirmed) return;
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    try {
      if (!window.crypto?.randomUUID)
        throw Error(
          "Secure browser UUID support required: use HTTPS or localhost",
        );
      requestKey.current ||= window.crypto.randomUUID();
      await apiRequest(`${base}/loss-requests`, {
        method: "POST",
        accessToken,
        body: {
          ...form,
          requestKey: requestKey.current,
          previewHash: preview.previewHash,
          requestVerified: true,
        },
      });
      if (seq !== generation.current) return;
      setNotice(
        "Request submitted for independent approval. No balances changed.",
      );
      await load();
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  const candidate = data?.requests.find((r) => r.id === selected);
  async function approve() {
    if (
      busy ||
      !candidate ||
      !canApprove ||
      !actor ||
      candidate.createdBy === actor ||
      !approvalConfirmed ||
      approvalNote.trim().length < 20
    )
      return;
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    try {
      const r = await apiRequest(
        `${base}/loss-requests/${candidate.id}/approve`,
        {
          method: "POST",
          accessToken,
          body: {
            previewHash: candidate.payloadHash,
            approvalNote,
            approvalVerified: true,
          },
        },
      );
      if (seq !== generation.current) return;
      setNotice(
        "Approved loss posted. Stock, supplier dues and cash unchanged.",
      );
      if (onPosted) {
        onPosted(r);
        return;
      }
      await load();
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  const summary = (s) => (
    <div className="space-y-2 text-sm">
      <p>
        Goods loss ₹{s.remaining.goods} · Total expense ₹{s.totalExpense} ·
        Posting date {s.date}
      </p>
      <p>
        Reason: {s.reason} · Evidence: {s.evidenceReference}
      </p>
      <p>
        Tax treatment: {s.taxTreatment} · Review: {s.taxReviewNote}
      </p>
      <p>
        Remaining original CGST {s.remaining.cgst}, SGST {s.remaining.sgst},
        IGST {s.remaining.igst}. GST treatment is an explicit accountant
        decision, not an automatic eligibility determination or filing.
      </p>
      <p>
        Expense ledger:{" "}
        {data?.expenseLedgers.find((l) => l.id === s.expenseLedgerId)?.name ||
          s.expenseLedgerId}
      </p>
      <table>
        <thead>
          <tr>
            <th>Ledger</th>
            <th>Debit</th>
            <th>Credit</th>
          </tr>
        </thead>
        <tbody>
          {s.lines?.map((l, i) => (
            <tr key={i}>
              <td>
                {data?.expenseLedgers.find((x) => x.id === l.ledgerId)?.name ||
                  l.ledgerId}
              </td>
              <td>{l.debit}</td>
              <td>{l.credit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <details className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6">
      <summary className="cursor-pointer text-xl font-semibold">
        Approved supplier-return loss
      </summary>
      <p className="text-sm text-gray-600">
        Rejected goods remain pending by default. A loss request covers the
        entire remaining goods value after full supplier rejection. A different
        authorised user must approve it. Requesting posts nothing; approval
        posts loss expense against clearing and only the explicitly reviewed tax
        treatment. Stock, supplier dues and cash do not change.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={load}
        className="h-11 rounded-xl border px-5"
      >
        Load Loss Requests
      </button>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {notice}
        </p>
      )}
      {data?.loss ? (
        <div>
          <h3 className="font-semibold">
            Goods written off · {data.loss.voucher.number}
          </h3>
          {summary(data.loss.request.snapshot)}
          <p className="text-sm">
            Independent approval retained. Further credits, dispute edits and
            isolated reversal are blocked.
          </p>
        </div>
      ) : (
        data && (
          <>
            {canRequest && (
              <form onSubmit={review} className="space-y-4">
                <fieldset disabled={busy} className="grid gap-4 md:grid-cols-2">
                  <label>
                    Loss posting date
                    <input
                      required
                      type="date"
                      className={input}
                      value={form.date}
                      onChange={(e) => change("date", e.target.value)}
                    />
                  </label>
                  <label>
                    Separate loss expense ledger
                    <select
                      required
                      className={input}
                      value={form.expenseLedgerId}
                      onChange={(e) =>
                        change("expenseLedgerId", e.target.value)
                      }
                    >
                      <option value="">Select expense ledger</option>
                      {data.expenseLedgers.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.code} · {l.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Accountant-reviewed tax treatment
                    <select
                      required
                      className={input}
                      value={form.taxTreatment}
                      onChange={(e) => change("taxTreatment", e.target.value)}
                    >
                      <option value="">No automatic tax decision</option>
                      <option value="KEEP_INPUT_TAX">
                        Keep input-tax entries unchanged after review
                      </option>
                      <option value="REVERSE_INPUT_TAX_TO_LOSS">
                        Reverse remaining input tax to loss expense
                      </option>
                    </select>
                  </label>
                  <label>
                    Loss evidence reference
                    <input
                      required
                      minLength={3}
                      maxLength={150}
                      className={input}
                      value={form.evidenceReference}
                      onChange={(e) =>
                        change("evidenceReference", e.target.value)
                      }
                    />
                  </label>
                  {[
                    ["reason", "Permanent loss reason", 10],
                    ["taxReviewNote", "Accountant tax review note", 20],
                  ].map(([k, label, min]) => (
                    <label key={k}>
                      {label}
                      <textarea
                        required
                        minLength={min}
                        maxLength={1000}
                        className="mt-2 min-h-24 w-full rounded-xl border p-3"
                        value={form[k]}
                        onChange={(e) => change(k, e.target.value)}
                      />
                    </label>
                  ))}
                </fieldset>
                <button disabled={busy} className="h-11 rounded-xl border px-5">
                  Preview Loss Request
                </button>
              </form>
            )}
            {preview && (
              <section className="space-y-3 rounded-xl bg-amber-50 p-4">
                {summary(preview.snapshot)}
                <label className="flex gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    disabled={busy}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  I verified full rejection, loss expense, evidence and tax
                  review; a different approver must authorise posting.
                </label>
                <button
                  type="button"
                  disabled={busy || !confirmed}
                  onClick={request}
                  className="h-11 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-50"
                >
                  Submit Loss Request
                </button>
              </section>
            )}
            <section className="space-y-3">
              <h3 className="font-semibold">Independent approval</h3>
              <p className="text-xs text-gray-500">
                Latest 100 immutable requests. Unposted requests may be stale
                and are revalidated before approval. No approval permission is
                automatically granted.
              </p>
              <label>
                Loss request
                <select
                  className={input}
                  disabled={busy}
                  value={selected}
                  onChange={(e) => {
                    setSelected(e.target.value);
                    setApprovalNote("");
                    setApprovalConfirmed(false);
                  }}
                >
                  <option value="">Select reviewed request</option>
                  {data.requests.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.snapshot.date} · Expense {r.snapshot.totalExpense} ·{" "}
                      {r.id}
                    </option>
                  ))}
                </select>
              </label>
              {candidate && (
                <div className="space-y-3">
                  {summary(candidate.snapshot)}
                  {candidate.createdBy === actor ? (
                    <p className="text-amber-800">
                      You cannot approve your own request, including as super
                      administrator.
                    </p>
                  ) : canApprove && actor ? (
                    <>
                      <label>
                        Independent approval note
                        <textarea
                          minLength={20}
                          maxLength={1000}
                          disabled={busy}
                          className="mt-2 min-h-24 w-full rounded-xl border p-3"
                          value={approvalNote}
                          onChange={(e) => {
                            setApprovalNote(e.target.value);
                            setApprovalConfirmed(false);
                          }}
                        />
                      </label>
                      <label className="flex gap-3 text-sm">
                        <input
                          type="checkbox"
                          disabled={busy}
                          checked={approvalConfirmed}
                          onChange={(e) =>
                            setApprovalConfirmed(e.target.checked)
                          }
                        />
                        I independently approve this full loss, expense and
                        specified tax treatment after reviewing the evidence.
                      </label>
                      <button
                        type="button"
                        disabled={
                          busy ||
                          !approvalConfirmed ||
                          approvalNote.trim().length < 20
                        }
                        onClick={approve}
                        className="h-11 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-50"
                      >
                        Approve &amp; Post Loss
                      </button>
                    </>
                  ) : (
                    <p>
                      Explicit loss-approval permission and a different reviewer
                      are required.
                    </p>
                  )}
                </div>
              )}
            </section>
          </>
        )
      )}
    </details>
  );
}
